import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { HealthScoreCard } from "@/components/HealthScoreCard";
import { BusinessGuardianCard } from "@/components/BusinessGuardianCard";
import {
  TrendingUp, TrendingDown, Wallet, AlertTriangle, Clock,
  Package, Briefcase, Plane, Receipt, ShoppingCart, FileText, Calendar
} from "lucide-react";

const API = process.env.REACT_APP_API || "";

const fmt = (v, currency = "EUR") => {
  try { return (v || 0).toLocaleString("de-DE", { style: "currency", currency }); }
  catch { return `${Number(v || 0).toLocaleString("de-DE")} ${currency}`; }
};

export function BusinessCockpit({ hasFeature, setTab }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = useCallback(() => {
    const t = localStorage.getItem("token") || sessionStorage.getItem("token");
    return { Authorization: `Bearer ${t}` };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/crossstudio/cockpit`, { headers: headers() });
        setData(r.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [headers]);

  if (loading) return <p className="text-sm text-gray-400 p-4">Lade Business Cockpit...</p>;
  if (!data) return <p className="text-sm text-gray-400 p-4">Cockpit nicht verfügbar.</p>;
  const currencyContext = data.currency_context || {};
  const formatMetric = (metric, legacyValue) => {
    const amounts = currencyContext.amounts_by_currency?.[metric];
    if (currencyContext.mixed_currencies && amounts) {
      const parts = Object.entries(amounts)
        .filter(([, value]) => Number(value || 0) !== 0)
        .map(([currency, value]) => fmt(value, currency));
      if (parts.length) return parts.join(" · ");
    }
    return fmt(legacyValue, currencyContext.base_currency || "EUR");
  };
  const formatCashflow = () => {
    if (currencyContext.mixed_currencies && currencyContext.cashflow_by_currency) {
      const parts = Object.entries(currencyContext.cashflow_by_currency)
        .filter(([, value]) => Number(value || 0) !== 0)
        .map(([currency, value]) => fmt(value, currency));
      return parts.length ? parts.join(" · ") : "Noch keine Werte";
    }
    return fmt(data.cashflow_monat, currencyContext.base_currency || "EUR");
  };
  const cashflowNegative = !currencyContext.mixed_currencies && data.cashflow_monat < 0;

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold">Business Cockpit</h2>

      <HealthScoreCard setTab={setTab} />
      <BusinessGuardianCard setTab={setTab} />
      {currencyContext.mixed_currencies && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="cockpit-currency-notice">
          <strong>Mehrere Währungen erkannt:</strong> EUR- und CHF-Beträge werden getrennt ausgewiesen. Es wird kein Wechselkurs verwendet und keine Summe künstlich umgerechnet.
        </div>
      )}

      {/* Hauptkennzahlen */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-gray-500"><TrendingUp className="w-4 h-4 text-green-500" /> Umsatz (Monat)</div>
          <div className="text-2xl font-bold mt-1">{formatMetric("umsatz_monat", data.umsatz_monat)}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-gray-500"><Wallet className="w-4 h-4 text-blue-500" /> Umsatz (Jahr)</div>
          <div className="text-2xl font-bold mt-1">{formatMetric("umsatz_jahr", data.umsatz_jahr)}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-sm text-gray-500"><TrendingDown className="w-4 h-4 text-red-500" /> Ausgaben (Monat)</div>
          <div className="text-2xl font-bold mt-1 text-red-600">{formatMetric("ausgaben_monat", data.ausgaben_monat)}</div>
        </Card>
        <Card className={`p-4 ${cashflowNegative ? "bg-red-50" : ""}`}>
          <div className="flex items-center gap-2 text-sm text-gray-500">{cashflowNegative ? <TrendingDown className="w-4 h-4 text-red-500" /> : <TrendingUp className="w-4 h-4 text-green-500" />} Cashflow (Monat)</div>
          <div className={`text-2xl font-bold mt-1 ${cashflowNegative ? "text-red-600" : "text-green-600"}`}>{formatCashflow()}</div>
        </Card>
      </div>

      {/* Forderungen & Verbindlichkeiten */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="text-sm text-gray-500">Offene Forderungen</div>
          <div className="text-xl font-bold mt-1">{formatMetric("offene_forderungen", data.offene_forderungen)}</div>
        </Card>
        <Card className={`p-4 ${data.ueberfaellige_rechnungen > 0 ? "bg-red-50 border-red-200" : ""}`}>
          <div className="flex items-center gap-1 text-sm text-gray-500">{data.ueberfaellige_rechnungen > 0 && <AlertTriangle className="w-3 h-3 text-red-500" />} Überfällige Rechnungen</div>
          <div className={`text-xl font-bold mt-1 ${data.ueberfaellige_rechnungen > 0 ? "text-red-600" : ""}`}>{data.ueberfaellige_rechnungen}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-gray-500">Offene Eingangsrechnungen</div>
          <div className="text-xl font-bold mt-1">{data.offene_eingangsrechnungen}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-gray-500">Einnahmen (Monat)</div>
          <div className="text-xl font-bold mt-1 text-green-600">{currencyContext.mixed_currencies ? Object.entries(currencyContext.income_by_currency || {}).filter(([, value]) => Number(value || 0) !== 0).map(([currency, value]) => fmt(value, currency)).join(" · ") || "Noch keine Werte" : fmt(data.einnahmen_monat, currencyContext.base_currency || "EUR")}</div>
        </Card>
      </div>

      {/* Projekte, Commerce, Workforce */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {hasFeature && hasFeature("workforce") && (
          <>
            <Card className="p-4">
              <div className="flex items-center gap-2 text-sm text-gray-500"><Briefcase className="w-3 h-3" /> Laufende Projekte</div>
              <div className="text-xl font-bold mt-1">{data.laufende_projekte}</div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-2 text-sm text-gray-500"><Clock className="w-3 h-3" /> Arbeitsstunden (Monat)</div>
              <div className="text-xl font-bold mt-1">{data.arbeitsstunden_monat} h</div>
            </Card>
          </>
        )}
        {hasFeature && hasFeature("commerce") && (
          <>
            <Card className="p-4">
              <div className="flex items-center gap-2 text-sm text-gray-500"><ShoppingCart className="w-3 h-3" /> Commerce Umsatz</div>
              <div className="text-xl font-bold mt-1">{formatMetric("commerce_umsatz", data.commerce_umsatz)}</div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-2 text-sm text-gray-500"><Package className="w-3 h-3" /> Offene Bestellungen</div>
              <div className="text-xl font-bold mt-1">{data.commerce_offen}</div>
            </Card>
          </>
        )}
        {hasFeature && hasFeature("reisestudio") && (
          <Card className="p-4">
            <div className="flex items-center gap-2 text-sm text-gray-500"><Plane className="w-3 h-3" /> Reisekosten (Monat)</div>
            <div className="text-xl font-bold mt-1">{formatMetric("reisekosten_monat", data.reisekosten_monat)}</div>
          </Card>
        )}
      </div>

      {/* Kommende Zahlungen & Fristen */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(data.kommende_zahlungen || []).length > 0 && (
          <Card className="p-4">
            <h3 className="font-semibold mb-2 flex items-center gap-2"><Receipt className="w-4 h-4 text-orange-500" /> Nächste fällige Zahlungen</h3>
            <div className="space-y-1.5">
              {data.kommende_zahlungen.map((z, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="truncate">{z.lieferant}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-gray-400">{z.faellig}</span>
                    <span className="font-medium">{fmt(z.betrag, z.currency || currencyContext.base_currency || "EUR")}</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
        {(data.wichtige_fristen || []).length > 0 && (
          <Card className="p-4">
            <h3 className="font-semibold mb-2 flex items-center gap-2"><Calendar className="w-4 h-4 text-purple-500" /> Wichtige Fristen (30 Tage)</h3>
            <div className="space-y-1.5">
              {data.wichtige_fristen.map((f, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="truncate">{f.partner} – {f.bezeichnung}</span>
                  <span className="text-xs text-gray-400 shrink-0">{f.endet}</span>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
